import React, { useState } from 'react'
import { Order } from '../../Types/Order'
import { Button } from '@instacart/tds'
import { Actions } from '~shared/lib/ActionsModal/ActionsModal'
import { CancelIcon, EditIcon, OptionsIcon } from '../../Assets/svg/Icons'
import { ScaleIcon } from '@instacart/ids-core'
import clsx from 'clsx'
import kdsStore from '../../Stores/KdsStore'
import { isActionAvailable } from '../../Utils/kdsActionPolicy'
import { KdsActionId } from '../../Types/KdsAuthorization'
import { stylesButton } from '~shared/lib/Button/Button'
import { customerOrderStatus, OrderChannel } from '~shared/util/constants'

interface DocketActionProps {
  order: Order
  config: any
  isDisabled: boolean
  buttonLabel: string
  callAction: () => Promise<void>
  confirmWeightAction: (event: React.MouseEvent<HTMLButtonElement>) => void
  fetchOrders?: () => void
  isProcessingPayment?: boolean
  onEditOrder?: () => void
}

export const DocketAction: React.FC<DocketActionProps> = ({
  order,
  config,
  isDisabled,
  buttonLabel,
  callAction,
  confirmWeightAction,
  fetchOrders,
  isProcessingPayment = false,
  onEditOrder,
}) => {
  const [error, setError] = useState('')
  const [actionInProgress, setActionInProgress] = useState(false)

  // Availability gates visibility here; the PIN-vs-device decision lives in cancelCustomerOrder.
  const canCancel = isActionAvailable(config?.actionPolicy, KdsActionId.CancelCustomerOrders)

  const handleOrderCancel = async () => {
    setActionInProgress(true)
    try {
      const cancelled = await kdsStore.cancelCustomerOrder(order.orderUid)
      // Refresh only when the order was actually cancelled -- a dismissed PIN prompt leaves it be.
      if (cancelled && fetchOrders) {
        fetchOrders()
      }
      return true
    } catch (error) {
      setError('Failed to cancel order. Please try again.')
      return false
    } finally {
      setActionInProgress(false)
    }
  }

  return (
    <div className="docket-action">
      <Button
        onClick={callAction}
        disabled={isDisabled || isProcessingPayment}
        variant={isProcessingPayment ? 'secondary' : 'primary'} // Secondary add for processing styling
        fullWidth
        loading={isProcessingPayment}
      >
        {buttonLabel}
      </Button>

      <Actions.Modal
        initialPage="/actions"
        actionInProgress={actionInProgress}
        trigger={({ toggleModal }) => (
          <Button
            onClick={e => {
              e.stopPropagation()
              toggleModal()
            }}
            className={clsx('options-button')}
            disabled={isDisabled || isProcessingPayment}
          >
            <OptionsIcon />
          </Button>
        )}
      >
        <Actions.Page header={`Order ${order.orderNo}`} path="/actions">
          {canCancel && (
            <Actions.Link
              to={(order.paymentTotal ?? 0) > 0 ? '/prevent-cancel' : '/cancel-order'}
              icon={<CancelIcon />}
              className={stylesButton.buttonSecondary}
            >
              Cancel Order
            </Actions.Link>
          )}
          {onEditOrder &&
            order.status !== customerOrderStatus.Complete &&
            order.status !== customerOrderStatus.Cancelled &&
            ((order.paymentTotal ?? 0) <= 0 || (order.balance ?? 0) > 0) &&
            (order.orderChannel === OrderChannel.Kiosk ||
              order.orderChannel === OrderChannel.KdsOrderEntry) && (
              <Actions.Button
                action={() => {
                  onEditOrder()
                  return true
                }}
                icon={<EditIcon />}
                className={stylesButton.buttonSecondary}
              >
                Edit Order
              </Actions.Button>
            )}
          {order.items.some(item => item.inThisDepartment && item.weightRequiresConfirmation) &&
            order.status != customerOrderStatus.New &&
            config.features.weightConfirmationRequired && (
              <Actions.Button
                action={e => {
                  e?.stopPropagation()
                  confirmWeightAction(e as React.MouseEvent<HTMLButtonElement>)
                  return true
                }}
                icon={<ScaleIcon />}
                className={stylesButton.buttonSecondary}
              >
                Confirm Weight
              </Actions.Button>
            )}
        </Actions.Page>
        <Actions.Page
          header={`Cancel Order ${order.orderNo}`}
          path="/cancel-order"
          invertCloseButton
        >
          <p>Are you sure you want to cancel this order?</p>
          <Actions.Button
            action={handleOrderCancel}
            disabled={actionInProgress}
            className={stylesButton.buttonDetrimental}
            linkFailure="/error"
          >
            Cancel Order
          </Actions.Button>
        </Actions.Page>
        <Actions.Page header={`Unable to Cancel Order`} path="/prevent-cancel">
          <p>You cannot cancel this order as a payment has already been processed.</p>
        </Actions.Page>
        <Actions.Page path="/error" header="An Error Occurred">
          <p>{error}</p>
        </Actions.Page>
      </Actions.Modal>
    </div>
  )
}
